'use client';

import { Loader2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../../../../components/ui/button';
import { Textarea } from '../../../../components/ui/textarea';
import { Comment } from '../../../../types/shared';
import { addSuggestionComment } from '../../lib/actions/suggestionActions';
import { addTourismComment } from '../../lib/actions/tourismActions';

type Props = {
  itemId: string;
  itemType: 'place' | 'suggestion';
  parentId?: string | null;
  onCommentAdded?: (newComment: Comment) => void;
};

export function AddCommentForm({ itemId, itemType, parentId, onCommentAdded }: Props) {
  const [text, setText] = useState('');
  const [isSending, setIsSending] = useState(false);

  const handleSubmit = useCallback(async () => {
    const clean = text.trim();
    if (!clean || isSending) return;

    const action = itemType === 'place' ? addTourismComment : addSuggestionComment;

    setIsSending(true);
    try {
      const result = await action(itemId, clean, parentId ?? undefined);

      if (result.status === 'success' && 'newComment' in result) {
        setText('');
        onCommentAdded?.(result.newComment);
        toast.success('Комментарий добавлен!');
        return;
      }

      if (result.status === 'warning') toast(result.message || 'Предупреждение', { icon: '⚠️' });
      else if (result.status === 'banned') toast.error(result.message || 'Доступ заблокирован.');
      else toast.error(result.message || 'Произошла ошибка.');
    } catch (e) {
      console.error(e);
      toast.error('Не удалось отправить комментарий.');
    } finally {
      setIsSending(false);
    }
  }, [text, isSending, itemId, itemType, parentId, onCommentAdded]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      className="flex flex-col gap-2"
    >
      <label htmlFor={`comment-form-${itemId}-${parentId || 'root'}`} className="sr-only">
        Ваш комментарий
      </label>

      <Textarea
        id={`comment-form-${itemId}-${parentId || 'root'}`}
        name="text"
        placeholder="Написать комментарий... (Ctrl+Enter)"
        required
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        className="bg-slate-700/50 border-slate-600 text-slate-100 placeholder:text-slate-400 focus:ring-sky-500 min-h-[80px] resize-y"
        disabled={isSending}
      />

      <div className="flex justify-end">
        <Button
          type="submit"
          size="sm"
          disabled={isSending || !text.trim()}
          className="bg-sky-600 hover:bg-sky-700 text-white transition-colors"
        >
          {isSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isSending ? 'Отправка...' : 'Отправить'}
        </Button>
      </div>
    </form>
  );
}
